import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, ArrowRight, Clock, Activity, PlayCircle, TrendingUp, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { themedHex, fmtDate } from "@/lib/format";
import KpiTiles from "@/components/KpiTiles";
import PipelineGraph from "@/components/PipelineGraph";
import LaneTrends from "@/components/LaneTrends";
import ReferralTable from "@/components/ReferralTable";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const TOUR_VIDEO = `${process.env.PUBLIC_URL || ""}/demo/glh-leadership-demo.mp4`;

const OUTCOME_STATUSES = ["In-progress", "Monitoring", "On-hold"];

function sevText(s) {
  return s === "red" ? "text-red-400" : s === "yellow" ? "text-yellow-400" : "text-cyan-400";
}

function TouchpointBoard({ title, icon: Icon, accent, items, empty, onOpen, testid, meta }) {
  return (
    <div className={`glass rounded-xl p-4 ${accent}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-head font-bold text-sm uppercase tracking-wide flex items-center gap-2">
          <Icon className="w-4 h-4" /> {title}
        </h2>
        <span className="text-xs font-mono text-muted-foreground" data-testid={`${testid}-count`}>{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground font-mono py-2">{empty}</p>
      ) : (
        <div className="space-y-1">
          {items.slice(0, 6).map((x) => (
            <button key={x.id} onClick={() => onOpen(x.id)} data-testid={`${testid}-row-${x.id}`}
                    className="w-full flex items-center justify-between gap-3 text-left row-hover rounded-md px-2 py-1.5 border border-white/8">
              <span className="font-mono text-cyan-300 text-sm shrink-0">{x.id}</span>
              <span className="text-xs text-muted-foreground font-mono truncate flex-1">{x.initials} · {x.lane}</span>
              <span className={`text-xs font-mono shrink-0 ${sevText(x.aging.severity)}`}>{meta ? meta(x) : `${x.aging.business_days_open}d`}</span>
            </button>
          ))}
          {items.length > 6 && <p className="text-[11px] font-mono text-muted-foreground pt-1">+{items.length - 6} more</p>}
        </div>
      )}
    </div>
  );
}

function ThroughTile({ label, value, color, testid }) {
  const { theme } = useTheme();
  const c = themedHex(color, theme);
  return (
    <div className="rounded-lg bg-slate-900/40 border border-white/8 px-3 py-2.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-mono">{label}</div>
      <div className="mt-1 font-mono font-medium text-2xl md:text-3xl tnum tracking-tight" style={{ color: c }}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [period, setPeriod] = useState("fy");
  const [cstart, setCstart] = useState("");
  const [cend, setCend] = useState("");
  const [tourOpen, setTourOpen] = useState(false);
  const [watch, setWatch] = useState([]);
  const [open, setOpen] = useState([]);
  const [pending, setPending] = useState([]);

  const load = async (p = period, s = cstart, e = cend) => {
    if (p === "custom" && !s) return;  // wait for a start date
    const params = p === "custom" ? { period: p, start: s, end: e || undefined } : { period: p };
    const [d, r] = await Promise.all([
      api.get("/dashboard", { params }),
      api.get("/referrals", { params: { open_only: true } }),
    ]);
    setDash(d.data);
    const rows = r.data.filter((x) => !x.out_of_summary);
    setOpen(rows);
    setPending(r.data.filter((x) => x.status === "Pending Disposition")
      .sort((a, b) => String(a.recheck_date || "").localeCompare(String(b.recheck_date || ""))));
    setWatch(r.data.filter((x) => !x.out_of_summary && (x.aging.severity === "red" || x.aging.stale || x.aging.severity === "yellow")).slice(0, 12));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const changePeriod = (p) => { setPeriod(p); if (p !== "custom") load(p); };

  // Onboarding nudge: auto-open the tour once for a new user, then never again.
  // Open first; write the "seen" flag only on close so React StrictMode's dev
  // double-mount can't set the flag before the dialog is actually shown.
  useEffect(() => {
    if (!user?.email) return;
    if (!localStorage.getItem(`glh_tour_seen_${user.email}`)) setTourOpen(true);
  }, [user?.email]);

  const handleTourChange = (openVal) => {
    setTourOpen(openVal);
    if (!openVal && user?.email) localStorage.setItem(`glh_tour_seen_${user.email}`, "1");
  };

  const byAge = (a, b) => b.aging.business_days_open - a.aging.business_days_open;
  const awaitingConfirm = open.filter((x) => x.status === "New").sort(byAge);
  const awaitingOutcome = open.filter((x) => OUTCOME_STATUSES.includes(x.status)).sort(byAge);

  return (
    <div className="p-6 space-y-5" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head font-black text-3xl tracking-tight">Operations Dashboard</h1>
          <p className="text-sm text-muted-foreground font-mono mt-1">{dash?.fiscal_week} · live aging on business days (US federal holidays excluded)</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={changePeriod}>
            <SelectTrigger className="w-[150px] bg-slate-900/60 border-white/10 h-9 text-sm" data-testid="dashboard-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="fy">This Fiscal Year</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="custom">Custom range…</SelectItem>
            </SelectContent>
          </Select>
          {period === "custom" && (
            <div className="flex items-center gap-1.5" data-testid="dashboard-custom-range">
              <input type="date" value={cstart} max={cend || undefined}
                     onChange={(e) => { setCstart(e.target.value); load("custom", e.target.value, cend); }}
                     data-testid="dashboard-custom-start"
                     className="bg-slate-900/60 border border-white/10 rounded-md h-9 px-2 text-sm text-foreground" />
              <span className="text-muted-foreground text-xs">→</span>
              <input type="date" value={cend} min={cstart || undefined}
                     onChange={(e) => { setCend(e.target.value); load("custom", cstart, e.target.value); }}
                     data-testid="dashboard-custom-end"
                     className="bg-slate-900/60 border border-white/10 rounded-md h-9 px-2 text-sm text-foreground" />
            </div>
          )}
          <Button onClick={() => setTourOpen(true)} data-testid="watch-tour-btn" variant="outline"
                  className="gap-2 border-cyan-500/40 text-cyan-600 dark:text-cyan-300 hover:bg-cyan-500/10 font-semibold">
            <PlayCircle className="w-4 h-4" /> Watch the tour
          </Button>
          <Button onClick={() => navigate("/report")} data-testid="run-report-btn" className="bg-violet-500 hover:bg-violet-400 text-white font-semibold gap-2">
            <FileText className="w-4 h-4" /> Run Pre-IDT Report <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Dialog open={tourOpen} onOpenChange={handleTourChange}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-slate-950 border-white/10" data-testid="tour-dialog">
          <DialogHeader className="px-5 pt-4 pb-2">
            <DialogTitle className="font-head text-base">Product tour · how the referral tracker works</DialogTitle>
            <DialogDescription className="sr-only">A short guided walkthrough of the referral tracker, from intake through outcome and analytics.</DialogDescription>
          </DialogHeader>
          <video key={tourOpen ? "open" : "closed"} src={TOUR_VIDEO} controls autoPlay playsInline
                 data-testid="tour-video" className="w-full aspect-video bg-black" />
          <p className="px-5 py-3 text-xs text-muted-foreground font-mono">~5 min · intake → confirmation → outcome → analytics → leadership digest</p>
        </DialogContent>
      </Dialog>

      {dash?.throughput && (
        <section className="glass rounded-xl p-4" data-testid="throughput-card">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-head font-bold text-sm uppercase tracking-wide text-muted-foreground flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-400" /> Throughput · {dash.period_label}
            </h2>
            <span className="text-[11px] font-mono text-muted-foreground">{dash.range?.start || "earliest"} → {dash.range?.end}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <ThroughTile testid="tp-placed" label="Placed" value={dash.throughput.placed} color="#22D3EE" />
            <ThroughTile testid="tp-admitted" label="Admitted" value={dash.throughput.admitted} color="#34D399" />
            <ThroughTile testid="tp-notadmitted" label="Not admitted" value={dash.throughput.not_admitted} color="#F87171" />
            <ThroughTile testid="tp-conversion" label="Conversion" value={dash.throughput.conversion_rate == null ? "—" : `${dash.throughput.conversion_rate}%`} color="#A78BFA" />
            <ThroughTile testid="tp-cycle" label="Median cycle" value={dash.throughput.median_cycle_biz == null ? "—" : `${dash.throughput.median_cycle_biz}d`} color="#F59E0B" />
          </div>
          <p className="text-[11px] text-muted-foreground font-mono mt-3">Admissions &amp; decisions counted when they occurred; placements by referral date.</p>
        </section>
      )}

      <div>
        <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-mono mb-2">Live backlog · current open items (not time-filtered)</p>
        <KpiTiles k={dash?.kpis} />
      </div>

      <div className="grid md:grid-cols-3 gap-4" data-testid="touchpoint-board">
        <TouchpointBoard title="Awaiting confirmation" icon={Clock} accent="glass-cyan"
                         items={awaitingConfirm} empty="Nothing waiting to be confirmed." testid="board-confirm"
                         onOpen={(id) => navigate(`/referrals/${id}`)} />
        <TouchpointBoard title="Awaiting outcome" icon={Activity} accent=""
                         items={awaitingOutcome} empty="Nothing waiting on an outcome." testid="board-outcome"
                         onOpen={(id) => navigate(`/referrals/${id}`)} />
        <TouchpointBoard title={`Pending disposition${dash?.kpis?.pending_overdue ? ` · ${dash.kpis.pending_overdue} overdue` : ""}`}
                         icon={CalendarClock} accent="" items={pending} empty="Nothing pending a follow-up." testid="board-pending"
                         meta={(x) => (x.aging.followup_overdue ? `${x.aging.followup_overdue_days}d overdue` : `f/u ${fmtDate(x.recheck_date)}`)}
                         onOpen={(id) => navigate(`/referrals/${id}`)} />
      </div>

      <PipelineGraph pipeline={dash?.pipeline || []} uncounted={dash?.uncounted || []} onLaneClick={(lane) => navigate(`/referrals?lane=${encodeURIComponent(lane)}`)} />

      <LaneTrends />

      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-head font-bold text-sm uppercase tracking-[0.18em] text-muted-foreground">Aging Watch — needs attention</h2>
          <button onClick={() => navigate("/referrals")} className="text-xs font-mono text-cyan-400 hover:text-cyan-300" data-testid="view-all-referrals">view all →</button>
        </div>
        <ReferralTable rows={watch} testid="aging-watch-table" />
      </div>
    </div>
  );
}
